import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Container, Row, Col, Button, Alert } from 'react-bootstrap';
import { appointmentApi } from '../services/api';

const BookingConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointmentId');
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!appointmentId) {
      setError('No appointment ID provided');
      setLoading(false);
      return;
    }

    const fetchAppointment = async () => {
      try {
        setLoading(true);
        const response = await appointmentApi.getOne(appointmentId);
        setAppointment(response.data.data.appointment);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [appointmentId]);

  if (loading) {
    return <div>Loading appointment details...</div>;
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Alert variant="danger">{error}</Alert>
            <div className="text-center mt-3">
              <Button variant="primary" href="/">Return to Home</Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-4">
                <h2 className="text-success">✓ Appointment Confirmed</h2>
              </div>

              <Card.Title className="mb-4">Thank you for your booking!</Card.Title>

              <Card className="mb-4 text-start">
                <Card.Body>
                  <Card.Title>Appointment Details</Card.Title>

                  <div className="mb-3">
                    <strong>Service:</strong> {appointment?.serviceId?.name}
                  </div>

                  <div className="mb-3">
                    <strong>Date & Time:</strong> {new Date(appointment?.startTime).toLocaleString()}
                  </div>

                  <div className="mb-3">
                    <strong>Duration:</strong> {appointment?.serviceId?.duration} minutes
                  </div>

                  <div className="mb-3">
                    <strong>Customer:</strong> {appointment?.customerName}
                  </div>

                  {appointment?.customerEmail && (
                    <div className="mb-3">
                      <strong>Email:</strong> {appointment?.customerEmail}
                    </div>
                  )}

                  {appointment?.customerPhone && (
                    <div className="mb-3">
                      <strong>Phone:</strong> {appointment?.customerPhone}
                    </div>
                  )}

                  {appointment?.notes && (
                    <div className="mb-3">
                      <strong>Notes:</strong> {appointment?.notes}
                    </div>
                  )}
                </Card.Body>
              </Card>

              <div className="mt-4">
                <p>You will receive a confirmation email shortly.</p>
                <p>You can add this appointment to your calendar:</p>
                <Button variant="outline-secondary" size="sm" className="me-2">
                  Add to Google Calendar
                </Button>
                <Button variant="outline-secondary" size="sm">
                  Add to Outlook
                </Button>
              </div>

              <div className="mt-4">
                <Button variant="primary" href="/" className="me-2">
                  Return to Home
                </Button>
                <Button variant="outline-primary" href="/">
                  Book Another Appointment
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BookingConfirmationPage;
